import {
  colorValues,
  customAppearanceModelValues,
  customAppearanceOrderValues,
  CustomEventInfoAppearanceSectionValues,
  customPdfAppearanceSectionsValues,
} from "@ac-expo/api-types";
import { z } from "zod";
import { fileSchema } from "../files";

export const CustomPdfAppearanceSchema = z.object({
  id: z.string().nullable(),
  backgroundPicture: fileSchema.nullable(),
  headerPicture: fileSchema.nullable(),
  backgroundColor: z.string(),
  opacity: z.number().min(0).max(100),
  headerColor: z.string(),
  textColor: z.string(),
  model: z.enum(customAppearanceModelValues),
  pdfSectionsOrder: z.array(z.enum(customPdfAppearanceSectionsValues)),
  customSection1: z.string().nullable(),
  customSection2: z.string().nullable(),
  xBackgroundPicture: z.number(),
  yBackgroundPicture: z.number(),
  hBackgroundPicture: z.number(),
  wBackgroundPicture: z.number(),
  xHeaderPicture: z.number(),
  yHeaderPicture: z.number(),
  hHeaderPicture: z.number(),
  wHeaderPicture: z.number(),
  textPictures: z.array(fileSchema),
});

export const CustomAppearanceSchema = z.object({
  id: z.string().nullable(),
  color: z.string(),
  picture: fileSchema.nullable(),
  order: z.enum(customAppearanceOrderValues),
  xPicture: z.number(),
  yPicture: z.number(),
  hPicture: z.number(),
  wPicture: z.number(),
  initialHPicture: z.number(),
  initialWPicture: z.number(),
});

export const CustomEventAppearanceSchema = z.object({
  id: z.string().nullable(),
  backgroundPicture: fileSchema.nullable(),
  backgroundColor: z.enum(colorValues),
  opacity: z.number().min(0).max(100),
  model: z.number(),
  countdown: z.boolean(),
  eventInfoSections: z.array(z.enum(CustomEventInfoAppearanceSectionValues)),
});

export type CustomAppearance = z.infer<typeof CustomAppearanceSchema>;
export type CustomPdfAppearance = z.infer<typeof CustomPdfAppearanceSchema>;
export type CustomEventAppearance = z.infer<typeof CustomEventAppearanceSchema>;

export const defaultCustomAppearanceValues: CustomAppearance = {
  color: "#FFFFFF",
  order: "qrCodeLast",
  picture: null,
  id: null,
  xPicture: 0,
  yPicture: 0,
  hPicture: 0,
  wPicture: 0,
  initialHPicture: 0,
  initialWPicture: 0,
};

export const defaultCustomPdfAppearanceValues: CustomPdfAppearance = {
  backgroundColor: "#FFFFFF",
  textColor: "#000000",
  headerColor: "#000000",
  model: "fourQuarters",
  backgroundPicture: null,
  opacity: 100,
  headerPicture: null,
  id: null,
  pdfSectionsOrder: [
    "qrCode",
    "customSection1",
    "customSection2",
    "conditions",
  ],
  textPictures: [],
  customSection1: null,
  customSection2: null,
  hBackgroundPicture: 0,
  wBackgroundPicture: 0,
  xBackgroundPicture: 0,
  yBackgroundPicture: 0,
  hHeaderPicture: 0,
  wHeaderPicture: 0,
  xHeaderPicture: 0,
  yHeaderPicture: 0,
};

export const defaultCustomEventAppearanceValues: CustomEventAppearance = {
  backgroundColor: "white",
  model: 0,
  backgroundPicture: null,
  opacity: 100,
  id: null,
  countdown: false,
  eventInfoSections: [
    "mainCharacteristics",
    "locations",
    "dates",
    "pictures",
    "links",
  ],
};

export const UpsertCustomPdfAppearanceTemplateSchema = z.object({
  templateName: z.string().min(1).max(50),
  slot: z.number().min(0).max(4),
});

export const defaultUpsertCustomPdfAppearanceTemplateValues = {
  templateName: "",
  slot: 0,
} as const;

export type UpsertCustomPdfAppearanceTemplateForm = z.infer<
  typeof UpsertCustomPdfAppearanceTemplateSchema
>;

export type CustomPdfAppearanceTemplateForm = CustomPdfAppearance &
  UpsertCustomPdfAppearanceTemplateForm & {
    previewPicture: z.infer<typeof fileSchema> | null;
  };
